import { useEffect, useMemo } from 'react'
import { useQueries, UseQueryOptions } from 'react-query'
import { createDatasetExportJob, getExportJobStatus, loadLocalExportTasks } from '../api/exportJobs'
import { cancelActivityDataJob, getActivityDataJobStatus } from '../api/activityDataJobs'
import {
  ActivityDataExportJob,
  ActivityDataExportJobStatus,
  DataExportRequest,
  DataExportSource,
  ExportJob,
  ExportJobParams,
  ExportJobStatus,
  JobStatusType,
  JobType,
  LayerExportJob,
  LocalLayerDataExportJob
} from '../types'
import { useAppDispatch, useAppSelector } from 'app/state'
import { createSelector } from '@reduxjs/toolkit'
import { AppStore, RootState } from 'app/state/store'
import { removeExportJob, setExports, updateExportJob } from '../state/jobsExportingSlice'
import { createApiError, isApiError } from 'features/common/api/apiError'
import { getWithConditionalRetry } from 'features/common/api/utils'
import { queryClient } from 'queryClient'
import { deleteBatchJob } from '../api/batchApi'
import { ApiError } from 'features/common/types'
import { isPendingOrRunning } from './jobs'
import { isExportableJob, shouldRetryJobOnError } from '../utils'
import { POLLING_INTERVAL } from '../state/jobConstants'
import useBuilderMode from 'features/builder/hooks/useBuilderMode'

export const DEFAULT_EXPORT_EXPIRATION_MS = 24 * 60 * 60 * 1000

const exportFinishedJobStatusQuery = (
  job: ExportJob | ActivityDataExportJob
): UseQueryOptions<ExportJobStatus | ActivityDataExportJobStatus, Error> => {
  const keyParams = isExportableJob(job.type)
    ? [(job.jobParams as ExportJobParams)?.connectionName, (job.jobParams as ExportJobParams)?.jobId]
    : [job.id]

  return {
    queryKey: ['exportingJobs', ...keyParams],
    queryFn: async ({ signal }) =>
      getWithConditionalRetry<ExportJobStatus | ActivityDataExportJobStatus>({
        get: () => {
          switch (job.type) {
            case JobType.DatasetExport:
            case JobType.WorkflowDataExport:
              return getExportJobStatus(job.jobParams as ExportJobParams)
            case JobType.ActivityData:
              return getActivityDataJobStatus({ jobId: job.id })
          }
        },
        shouldRetry: (job) => isPendingOrRunning(job.status),
        shouldRetryError: shouldRetryJobOnError,
        interval: POLLING_INTERVAL,
        signal
      })
  }
}

const getPendingRemoteExportJobs = createSelector(
  (state: RootState) => state.jobsExporting.exports,
  (exports: (LayerExportJob | ActivityDataExportJob)[]) =>
    exports.filter(
      (e): e is ExportJob =>
        (e.type === JobType.DatasetExport ||
          e.type === JobType.ActivityData ||
          e.type === JobType.WorkflowDataExport) &&
        e.status === JobStatusType.Pending &&
        Boolean(e.jobParams)
    )
)

export function useExportJobs() {
  const dispatch = useAppDispatch()
  const remotePendingExports = useAppSelector(getPendingRemoteExportJobs)
  const exports = useAppSelector((state) => state.jobsExporting.exports)

  const remoteQueries = useMemo(
    () =>
      remotePendingExports.map(
        (e): UseQueryOptions<ExportJobStatus | ActivityDataExportJobStatus, Error> => ({
          ...exportFinishedJobStatusQuery(e),
          onSuccess: (result) => {
            dispatch(
              updateExportJob({
                id: e.id,
                remoteJobStatus: result,
                status: result.status
              })
            )
          },
          onError: (error) => {
            console.error('useExportJobs', error)
            const apiError = createApiError(error)
            dispatch(
              updateExportJob({
                id: e.id,
                status: JobStatusType.Failure,
                error: { msg: apiError.error, code: String(apiError.status) }
              })
            )
          }
        })
      ),
    [dispatch, remotePendingExports]
  )
  useQueries(remoteQueries)

  return exports
}

const jobExpirationTime = (job: LayerExportJob | ActivityDataExportJob) => {
  if (
    (job.type === JobType.DatasetExport || job.type === JobType.WorkflowDataExport) &&
    job.remoteJobStatus?.backendMetadata.expirationDate
  ) {
    return new Date(job.remoteJobStatus?.backendMetadata.expirationDate).getTime()
  } else {
    return job.createdAt + DEFAULT_EXPORT_EXPIRATION_MS
  }
}

const jobDownloadsExpirationTime = (job: LayerExportJob) => {
  if (
    (job.type === JobType.DatasetExport || job.type === JobType.WorkflowDataExport) &&
    job.remoteJobStatus?.downloadUrlsExpirationDate
  ) {
    return new Date(job.remoteJobStatus.downloadUrlsExpirationDate).getTime()
  }
  return undefined
}

/**
 * Execute layer-export jobs related lifeycycle effects.
 *
 * Should be mounted only once in the app.
 */
export function useExportJobsLifecycle() {
  const dispatch = useAppDispatch()
  const exports = useAppSelector((state) => state.jobsExporting.exports)
  const { isPublicMapLocation } = useBuilderMode()
  const userId = useAppSelector((state) => state.users.me?.user_id)

  // load saved tasks from local storage
  useEffect(() => {
    if (isPublicMapLocation || !userId) return

    const savedExports = loadLocalExportTasks(userId)
    dispatch(setExports(savedExports))
  }, [dispatch, isPublicMapLocation, userId])

  // remove tasks to be expired
  const firstExpirationTime = exports.reduce((r, e) => Math.min(r, jobExpirationTime(e)), Number.MAX_VALUE)
  useEffect(() => {
    if (firstExpirationTime === Number.MAX_VALUE) return

    const waitTime = Math.max(0, firstExpirationTime - Date.now())

    const timeout = setTimeout(() => {
      const now = Date.now()
      const expiredExports = exports.filter((e) => jobExpirationTime(e) <= now)
      expiredExports.forEach((e) => dispatch(removeExportJob(e.id)))
    }, waitTime)
    return () => clearTimeout(timeout)
  }, [dispatch, exports, firstExpirationTime])

  // reload jobs that have outdated downloadUrls
  const jobWithOldestDownloads = exports.reduce((r, job) => {
    if (job.type === JobType.DatasetExport && job.remoteJobStatus?.downloadUrlsExpirationDate) {
      const otherTime = r ? jobDownloadsExpirationTime(r) : 0
      const downloadUrlsExpirationTime = jobDownloadsExpirationTime(job)
      return !otherTime || (downloadUrlsExpirationTime && downloadUrlsExpirationTime < otherTime) ? job : r
    }
    return r
  }, undefined as ExportJob | undefined)

  useEffect(() => {
    if (!jobWithOldestDownloads || !jobWithOldestDownloads.remoteJobStatus?.downloadUrlsExpirationDate) {
      return
    }

    const waitTime = Math.max(0, (jobDownloadsExpirationTime(jobWithOldestDownloads) as number) - Date.now() - 5000)
    const timeout = setTimeout(() => {
      const jobParams = jobWithOldestDownloads.jobParams
      if (!jobParams) return

      queryClient.invalidateQueries(['exportingJobs', jobParams.connectionName, jobParams.jobId])
    }, waitTime)
    return () => clearTimeout(timeout)
  }, [dispatch, exports, jobWithOldestDownloads])
}

type ExportError = ApiError & { connectionName: string }

/**
 * Export datasets grouped by `connectionName`
 *
 * Returns created jobs or errors. Errors are grouped by connection, so for each connection there is only one entry.
 */
export async function exportDatasetsRemote({ sources }: DataExportRequest) {
  const sourcesByConnectionName = sources.reduce((result, entry) => {
    if (!result[entry.connectionName]) {
      result[entry.connectionName] = []
    }
    result[entry.connectionName].push(entry)
    return result
  }, {} as Record<string, DataExportSource[]>)

  return (
    await Promise.all(
      Object.entries(sourcesByConnectionName).map(([connectionName, sources]) =>
        createDatasetExportJob({
          connectionName,
          sources,
          accessToken: sources[0].accessToken
        }).catch((error) => {
          if (!isApiError(error)) {
            console.error('exportDatasetsRemote', error)
          }
          return { connectionName, ...createApiError(error) } as ExportError
        })
      )
    )
  ).flat()
}

export async function cancelExportJob({
  store,
  job
}: {
  store: AppStore
  job: ExportJob | LocalLayerDataExportJob | ActivityDataExportJob
}) {
  if (isExportableJob(job.type) && (job as ExportJob).jobParams) {
    await deleteBatchJob((job as ExportJob).jobParams as ExportJobParams)
  }
  if (job.type === JobType.ActivityData) {
    await cancelActivityDataJob({ jobId: job.id })
  }
  store.dispatch(removeExportJob(job.id))
}
